import PropTypes from "prop-types";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { gql, useMutation, useQuery, useSubscription } from "@apollo/client";

import SoftBox from "components/SoftBox";
import SoftBadge from "components/SoftBadge";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DataTableCustom from "components/Tables/DataTableCustom";

import { Card, Checkbox, Icon, MenuItem, Select, Tooltip } from "@mui/material";

import { MessageManager } from "context";
import { MenuContext } from "context/menuContext";

import ModalTarea from "./ModalTarea";
import ModalDetalleTarea from "./ModalDetalleTarea";
import ModalControlCafeteria from "./ModalControlCafeteria";
import ModalDetalleControlCafeteria from "./ModalDetalleControlCafeteria";
import { ConfirmarContext } from "context/ConfirmarContext";
import borders from "assets/theme/base/borders";
import colors from "assets/theme/base/colors";
import { getUproColor } from "utils/colors";

const CALENDAR_MONTHS = [
    "Ene.",
    "Feb.",
    "Mar.",
    "Abr.",
    "May.",
    "Jun.",
    "Jul.",
    "Ago.",
    "Sept.",
    "Oct.",
    "Nov.",
    "Dic.",
];

const CALENDAR_DAYS = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];

const GET_TAREAS = gql`
    query tareas($filter: JSON) {
        tareas(filter: $filter, order: { field: "fecha", order: "ASC" }) {
            id
            titulo
            observacion
            fecha
            estado
            periodicidad
            fechaLimite
            tipoTarea
            turno
            idUser
            responsable {
                id
                nombre
                apellido
                dni
            }
            tag {
                id
                nombre
            }
            items {
                id
                texto
                checked
                observacion
                tipoItem
                idMateriaPrima
                unidadAbreviatura
                cantidadInicial
                cantidadFinal
                pedidoCompras
            }
        }
    }
`;

const ELIMINAR_TAREA = gql`
  mutation eliminarTarea($id: ID!) {
    eliminarTarea(id: $id) {
      id
    }
  }
`;

const ANULAR_TAREA = gql`
    mutation anularTarea($id: ID!, $anular: Boolean!) {
        anularTarea(id: $id, anular: $anular) {
            id
            estado
        }
    }
`;

const TOGGLE_ITEM = gql`
    mutation toggleTareaItem($idItem: ID!, $checked: Boolean!) {
        toggleTareaItem(idItem: $idItem, checked: $checked) {
            id
            estado
            items {
                id
                texto
                checked
            }
        }
    }
`;

const SET_ALL_ITEMS = gql`
    mutation setAllTareaItemsChecked($idTarea: ID!, $checked: Boolean!) {
        setAllTareaItemsChecked(idTarea: $idTarea, checked: $checked) {
            id
            estado
            items {
                id
                texto
                checked
            }
        }
    }
`;

const SUB_TAREA_CREADA = gql`
    subscription {
        tareaCreada {
            id
            fecha
            periodicidad
            fechaLimite
            idUser
            idTag
            tipoTarea
            estado
        }
    }
`;

const SUB_TAREA_ACTUALIZADA = gql`
    subscription {
        tareaActualizada {
            id
            fecha
            idUser
            idTag
            tipoTarea
            estado
        }
    }
`;

const DOT_COLORS = {
    warning: "#ff9800",
    info: "#2196f3",
    error: "#f44336",
    dark: "#212121",
    success: "#4caf50",
};

function dotColorByEstado(estado) {
    switch (estado) {
        case "Pendiente":
        case "Incompleta":
            return DOT_COLORS.warning;
        case "En curso":
            return DOT_COLORS.info;
        case "Anulada":
            return DOT_COLORS.error;
        case "Vencida":
            return DOT_COLORS.dark;
        case "Finalizada":
            return DOT_COLORS.success;
        default:
            return DOT_COLORS.warning;
    }
}

function aggregateEstadoForDay(tasks) {
    if (!tasks?.length) return null;

    const priority = {
        Vencida: 5,
        Anulada: 4,
        Incompleta: 3,
        Pendiente: 2,
        "En curso": 1,
        Finalizada: 0,
    };

    let worst = tasks[0]?.estado;
    tasks.forEach((t) => {
        const e = t?.estado;
        if (!e) return;
        if (priority[e] > (priority[worst] ?? -1)) worst = e;
    });

    return worst || null;
}

function formatSelectedDate(date) {
    if (!date) return "Selecciona un día";
    return `${CALENDAR_DAYS[date.day()]}, ${date.date()} de ${CALENDAR_MONTHS[date.month()]} de ${date.year()}`;
}

function Calendario({ mode = "user", adminFilters = null }) {
    const { handleSnackbar } = useContext(MessageManager);
    const { confirmar } = useContext(ConfirmarContext);
    const { menu } = useContext(MenuContext);
    const { light } = colors;
    const { borderWidth } = borders;

    const [calendarMonth, setCalendarMonth] = useState(dayjs().month());
    const [calendarYear, setCalendarYear] = useState(dayjs().year());
    const [selectedDate, setSelectedDate] = useState(dayjs());

    const [openModalTarea, setOpenModalTarea] = useState(false);
    const [editingTask, setEditingTask] = useState(null);

    const [openModalControl, setOpenModalControl] = useState(false);
    const [editingControlTask, setEditingControlTask] = useState(null);

    // State for user details modal
    const [openModalDetalle, setOpenModalDetalle] = useState(false);
    const [detailsTask, setDetailsTask] = useState(null);

    const [openModalDetalleControl, setOpenModalDetalleControl] = useState(false);
    const [detailsControlTask, setDetailsControlTask] = useState(null);

    const monthStartIso = useMemo(
        () => dayjs().year(calendarYear).month(calendarMonth).startOf("month").format("YYYY-MM-DD"),
        [calendarYear, calendarMonth]
    );
    const monthEndIso = useMemo(
        () => dayjs().year(calendarYear).month(calendarMonth).endOf("month").format("YYYY-MM-DD"),
        [calendarYear, calendarMonth]
    );

    const where = useMemo(() => {
        const base = { fecha: { _between: [monthStartIso, monthEndIso] } };
        if (adminFilters) {
            if (mode === "admin" && adminFilters.idUser) base.idUser = adminFilters.idUser;
            if (adminFilters.idTag) base.idTag = adminFilters.idTag;
            if (adminFilters.tipoTarea) base.tipoTarea = adminFilters.tipoTarea;

            if (adminFilters.estado && adminFilters.estado.length > 0) {
                base.estado = { _in: adminFilters.estado };
            } else {
                base.estado = { _notIn: ["Anulada", "Finalizada"] };
            }
        }
        return base;
    }, [monthStartIso, monthEndIso, mode, adminFilters]);

    const { data, loading, refetch } = useQuery(GET_TAREAS, {
        variables: { filter: where },
        fetchPolicy: "network-only",
    });

    const { data: subCreada } = useSubscription(SUB_TAREA_CREADA);
    const { data: subActualizada } = useSubscription(SUB_TAREA_ACTUALIZADA);

    const isFechaInCurrentMonthRange = (fecha) => {
        if (!fecha) return false;
        const key = dayjs(fecha).format("YYYY-MM-DD");
        return key >= monthStartIso && key <= monthEndIso;
    };

    const doesRangeIntersectCurrentMonth = (startDate, endDate) => {
        if (!startDate) return false;
        const startKey = dayjs(startDate).format("YYYY-MM-DD");
        const endKey = endDate ? dayjs(endDate).format("YYYY-MM-DD") : startKey;
        return endKey >= monthStartIso && startKey <= monthEndIso;
    };

    useEffect(() => {
        const t = subCreada?.tareaCreada;
        if (!t) return;

        // Para tareas recurrentes publicamos una sola vez: refrescar si el rango de recurrencia toca el mes visible.
        if (t.periodicidad && t.periodicidad !== "UNICA") {
            if (!doesRangeIntersectCurrentMonth(t.fecha, t.fechaLimite)) return;
        } else {
            if (!isFechaInCurrentMonthRange(t.fecha)) return;
        }

        refetch();
    }, [subCreada, monthStartIso, monthEndIso, refetch]);

    useEffect(() => {
        const t = subActualizada?.tareaActualizada;
        if (!t) return;
        if (!isFechaInCurrentMonthRange(t.fecha)) return;
        refetch();
    }, [subActualizada, monthStartIso, monthEndIso, refetch]);

    const tareas = useMemo(() => data?.tareas || [], [data]);

    const yearOptions = useMemo(() => {
        const baseYear = dayjs().year();
        const years = Array.from({ length: 11 }, (_, index) => baseYear - 5 + index);
        if (!years.includes(calendarYear)) {
            years.push(calendarYear);
            years.sort((a, b) => a - b);
        }
        return years;
    }, [calendarYear]);

    const calendarDays = useMemo(() => {
        const monthStart = dayjs().year(calendarYear).month(calendarMonth).startOf("month");
        const firstDayOfMonth = monthStart.day();
        const daysInMonth = monthStart.daysInMonth();
        const prevMonth = monthStart.subtract(1, "month");
        const nextMonth = monthStart.add(1, "month");

        const days = [];

        for (let i = firstDayOfMonth - 1; i >= 0; i -= 1) {
            const dayNumber = prevMonth.daysInMonth() - i;
            const date = prevMonth.date(dayNumber);
            days.push({ date, current: false });
        }

        for (let day = 1; day <= daysInMonth; day += 1) {
            days.push({ date: monthStart.date(day), current: true });
        }

        const remainingCells = (7 - (days.length % 7)) % 7;
        for (let i = 1; i <= remainingCells; i += 1) {
            days.push({ date: nextMonth.date(i), current: false });
        }

        return days;
    }, [calendarMonth, calendarYear]);

    const tareasByDate = useMemo(() => {
        const map = new Map();
        tareas.forEach((t) => {
            const dateKey = dayjs(t.fecha).format("YYYY-MM-DD");
            if (!map.has(dateKey)) map.set(dateKey, []);
            map.get(dateKey).push(t);
        });
        return map;
    }, [tareas]);

    const selectedDateKey = selectedDate ? selectedDate.format("YYYY-MM-DD") : null;
    const selectedDayTasks = useMemo(() => {
        if (!selectedDateKey) return [];
        return tareasByDate.get(selectedDateKey) || [];
    }, [tareasByDate, selectedDateKey]);

    const [eliminarTarea, { loading: loadingDelete }] = useMutation(ELIMINAR_TAREA);
    const [anularTarea, { loading: loadingAnular }] = useMutation(ANULAR_TAREA);
    const [toggleItem, { loading: loadingToggle }] = useMutation(TOGGLE_ITEM);
    const [setAllItems, { loading: loadingSetAll }] = useMutation(SET_ALL_ITEMS);

    const handleCalendarMonthChange = (event) => {
        setCalendarMonth(Number(event.target.value));
        setSelectedDate(null);
    };

    const handleCalendarYearChange = (event) => {
        setCalendarYear(Number(event.target.value));
        setSelectedDate(null);
    };

    const handleDaySelection = (date) => {
        setSelectedDate(date);
    };

    const handleOpenAddMenu = (event) => {
        menu({
            open: event.currentTarget,
            align: "left",
            options: [
                {
                    name: "Checklist",
                    onClick: () => {
                        setEditingTask(null);
                        setOpenModalTarea(true);
                    },
                },
                {
                    name: "Control Cafetería",
                    onClick: () => {
                        setEditingControlTask(null);
                        setOpenModalControl(true);
                    },
                },
            ],
        });
    };

    const columns = useMemo(
        () => [
            {
                field: "titulo",
                width: "20%",
                headerName: "Título",
                renderCell: (row) => (
                    <SoftBox>
                        <SoftTypography variant="button" fontWeight="medium">
                            {row.titulo}
                        </SoftTypography>
                    </SoftBox>
                ),
            },
            {
                field: "tag",
                headerName: "Tag",
                // width: "15%",
                renderCell: (row) => (
                    <SoftBox>
                        <SoftTypography variant="button" fontWeight="medium">
                            {row.tag.nombre}
                        </SoftTypography>
                    </SoftBox>
                ),
            },
            {
                field: "estado",
                headerName: "Estado",
                // width: "25%",
                renderCell: (row) => (
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <Icon sx={{ fontSize: 10, color: dotColorByEstado(row.estado) }}>fiber_manual_record</Icon>
                        <SoftTypography variant="button">{row.estado}</SoftTypography>
                    </SoftBox>
                ),
            },
            ...mode === "admin" ? [{
                field: "responsable",
                headerName: "Responsable",
                width: "25%",
                renderCell: (row) => {
                    const u = row?.responsable;
                    return (
                        <SoftTypography variant="button">
                            {u ? `${u.apellido || ""}, ${u.nombre || ""}`.trim() : "-"}
                        </SoftTypography>
                    );
                },
            }] : [],
            {
                field: "",
                headerName: "",
                align: "center",
                // width: "9%",
                sortable: false,
                renderCell: (row) => {
                    const canEditDelete = mode === "admin" && row.estado === "Pendiente";
                    const isAnulada = row.estado === "Anulada";

                    const options = [];

                    if (mode === "user") {
                        options.push({
                            name: "Ver detalles",
                            icon: { icon: "visibility", color: getUproColor("uproBlue") },
                            onClick: () => {
                                if (row.tipoTarea === "CONTROL_CAFETERIA") {
                                    setDetailsControlTask(row);
                                    setOpenModalDetalleControl(true);
                                } else {
                                    setDetailsTask(row);
                                    setOpenModalDetalle(true);
                                }
                            },
                        });
                    }

                    if (mode === "admin") {
                        options.push({
                            name: isAnulada ? "Reactivar" : "Anular",
                            icon: {
                                icon: isAnulada ? "restart_alt" : "block",
                                color: isAnulada ? getUproColor("uproGreen") : getUproColor("uproYellow"),
                            },
                            disabled: loadingAnular,
                            onClick: async () => {
                                try {
                                    await anularTarea({ variables: { id: row.id, anular: !isAnulada } });
                                    handleSnackbar(isAnulada ? "Tarea reactivada" : "Tarea anulada", "success");
                                    await refetch();
                                } catch (e) {
                                    handleSnackbar(e?.message || "Error", "error");
                                }
                            },
                        });

                        options.push({
                            name: "Editar",
                            icon: { icon: "edit", color: getUproColor("uproBlue") },
                            disabled: !canEditDelete,
                            onClick: () => {
                                if (row.tipoTarea === "CONTROL_CAFETERIA") {
                                    setEditingControlTask(row);
                                    setOpenModalControl(true);
                                } else {
                                    setEditingTask(row);
                                    setOpenModalTarea(true);
                                }
                            },
                        });

                        options.push({
                            name: "Eliminar",
                            icon: { icon: "delete", color: getUproColor("uproPink") },
                            disabled: !canEditDelete || loadingDelete,
                            onClick: async () => {
                                const ok = await confirmar?.({
                                    titulo: "Eliminar tarea",
                                    mensaje: "¿Eliminar la tarea?",
                                });
                                if (!ok) return;
                                try {
                                    await eliminarTarea({ variables: { id: row.id } });
                                    handleSnackbar("Tarea eliminada", "success");
                                    await refetch();
                                } catch (e) {
                                    handleSnackbar(e?.message || "Error al eliminar", "error");
                                }
                            },
                        });
                    }

                    // if (mode === "user") {
                    //     return (
                    //         <SoftBox display="flex" justifyContent="center" gap={1}>
                    //             <Tooltip title="Ver detalles">
                    //                 <SoftButton
                    //                     variant="text"
                    //                     color="info"
                    //                     size="large"
                    //                     iconOnly
                    //                     circular
                    //                     onClick={() => {
                    //                         if (row.tipoTarea === "CONTROL_CAFETERIA") {
                    //                             setDetailsControlTask(row);
                    //                             setOpenModalDetalleControl(true);
                    //                         } else {
                    //                             setDetailsTask(row);
                    //                             setOpenModalDetalle(true);
                    //                         }
                    //                     }}
                    //                 >
                    //                     <Icon sx={{ fontSize: 18 }}>visibility</Icon>
                    //                 </SoftButton>
                    //             </Tooltip>
                    //         </SoftBox>
                    //     );
                    // }

                    // return (
                    //     <SoftBox display="flex" justifyContent="center" gap={1}>
                    //         <Tooltip title={isAnulada ? "Reactivar" : "Anular"}>
                    //             <span>
                    //                 <SoftButton
                    //                     variant="text"
                    //                     color={isAnulada ? "success" : "warning"}
                    //                     size="medium"
                    //                     iconOnly
                    //                     circular
                    //                     disabled={loadingAnular}
                    //                     sx={{ backgroundColor: isAnulada ? "#047ef8ff" : "#fc9700ff" }}
                    //                     onClick={async () => {
                    //                         try {
                    //                             await anularTarea({ variables: { id: row.id, anular: !isAnulada } });
                    //                             handleSnackbar(isAnulada ? "Tarea reactivada" : "Tarea anulada", "success");
                    //                             await refetch();
                    //                         } catch (e) {
                    //                             handleSnackbar(e?.message || "Error", "error");
                    //                         }
                    //                     }}
                    //                 >
                    //                     <Icon sx={{ fontSize: 18 }}>{isAnulada ? "restart_alt" : "block"}</Icon>
                    //                 </SoftButton>
                    //             </span>
                    //         </Tooltip>

                    //         <Tooltip title="Editar">
                    //             <span>
                    //                 <SoftButton
                    //                     variant="text"
                    //                     color="info"
                    //                     size="medium"
                    //                     iconOnly
                    //                     circular
                    //                     disabled={!canEditDelete}
                    //                     onClick={() => {
                    //                         if (row.tipoTarea === "CONTROL_CAFETERIA") {
                    //                             setEditingControlTask(row);
                    //                             setOpenModalControl(true);
                    //                         } else {
                    //                             setEditingTask(row);
                    //                             setOpenModalTarea(true);
                    //                         }
                    //                     }}
                    //                 >
                    //                     <Icon sx={{ fontSize: 18 }}>edit</Icon>
                    //                 </SoftButton>
                    //             </span>
                    //         </Tooltip>

                    //         <Tooltip title="Eliminar">
                    //             <span>
                    //                 <SoftButton
                    //                     variant="text"
                    //                     color="error"
                    //                     size="medium"
                    //                     iconOnly
                    //                     circular
                    //                     disabled={!canEditDelete || loadingDelete}
                    //                     onClick={async () => {
                    //                         const ok = await confirmar?.({
                    //                             titulo: "Eliminar tarea",
                    //                             mensaje: "¿Eliminar la tarea?",
                    //                         });
                    //                         if (!ok) return;
                    //                         try {
                    //                             await eliminarTarea({ variables: { id: row.id } });
                    //                             handleSnackbar("Tarea eliminada", "success");
                    //                             await refetch();
                    //                         } catch (e) {
                    //                             handleSnackbar(e?.message || "Error al eliminar", "error");
                    //                         }
                    //                     }}
                    //                 >
                    //                     <Icon sx={{ fontSize: 18 }}>delete</Icon>
                    //                 </SoftButton>
                    //             </span>
                    //         </Tooltip>
                    //     </SoftBox>
                    // );
                    return (
                        <SoftBox component="td" p={1} borderBottom={`${borderWidth[1]} solid ${light.main}`}>
                            <SoftBox display="flex" alignItems="center">
                                <Icon
                                    sx={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                        menu({
                                            open: e.currentTarget,
                                            align: "right",
                                            options,
                                        });
                                    }}
                                >
                                    more_vert
                                </Icon>
                            </SoftBox>
                        </SoftBox>
                    )
                },
            },
        ],
        [
            anularTarea,
            borderWidth,
            confirmar,
            eliminarTarea,
            handleSnackbar,
            light,
            loadingAnular,
            loadingDelete,
            loadingSetAll,
            mode,
            menu,
            refetch,
            setAllItems,
        ]
    );

    const expandedRowRender = (row) => {
        if (row?.tipoTarea === "CONTROL_CAFETERIA") {
            const items = row?.items || [];
            return (
                <SoftBox p={2} sx={{ backgroundColor: "#fafafa", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text">
                        Control cafetería
                    </SoftTypography>
                    <SoftBox mt={1} display="grid" gridTemplateColumns="1fr" gap={1}>
                        <SoftBox display="grid" gridTemplateColumns="1fr 2fr 1fr 1fr 1fr" gap={1}>
                            <SoftTypography variant="caption" fontWeight="bold">Tipo</SoftTypography>
                            <SoftTypography variant="caption" fontWeight="bold">Nombre</SoftTypography>
                            <SoftTypography variant="caption" fontWeight="bold">Cant. inicial</SoftTypography>
                            <SoftTypography variant="caption" fontWeight="bold">Cant. final</SoftTypography>
                            <SoftTypography variant="caption" fontWeight="bold">Pedido compras</SoftTypography>
                        </SoftBox>
                        {items.map((it) => (
                            <SoftBox key={it.id} display="grid" gridTemplateColumns="1fr 2fr 1fr 1fr 1fr" gap={1}>
                                <SoftTypography variant="caption">
                                    {it.tipoItem === "DESCARTABLE" ? "Descartable" : "Producto"}
                                </SoftTypography>
                                <SoftTypography variant="caption">{it.texto}</SoftTypography>
                                <SoftTypography variant="caption">
                                    {it.cantidadInicial ?? "-"} {it.unidadAbreviatura || ""}
                                </SoftTypography>
                                <SoftTypography variant="caption">
                                    {it.cantidadFinal ?? "-"} {it.unidadAbreviatura || ""}
                                </SoftTypography>
                                <SoftTypography variant="caption">
                                    {it.pedidoCompras ?? "-"} {it.unidadAbreviatura || ""}
                                </SoftTypography>
                            </SoftBox>
                        ))}
                        {!items.length && (
                            <SoftTypography variant="caption" color="text">
                                Sin items
                            </SoftTypography>
                        )}
                    </SoftBox>
                </SoftBox>
            );
        }

        const items = row?.items || [];
        return (
            <SoftBox p={2} sx={{ backgroundColor: "#fafafa", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                {row?.observacion ? (
                    <SoftBox mb={1}>
                        <SoftTypography variant="caption" fontWeight="bold" color="text">
                            Observación:
                        </SoftTypography>
                        <SoftTypography variant="caption" color="text" ml={1}>
                            {row.observacion}
                        </SoftTypography>
                    </SoftBox>
                ) : null}

                <SoftTypography variant="caption" fontWeight="bold" color="text">
                    Items
                </SoftTypography>

                <SoftBox mt={1} display="flex" flexDirection="column" gap={0.5}>
                    {items.length ? (
                        items.map((it) => {
                            const disabled = mode == "admin" || row.estado === "Anulada" || loadingToggle;

                            return (
                                <SoftBox key={it.id} display="flex" alignItems="center" gap={1}>
                                    <Checkbox
                                        size="small"
                                        checked={!!it.checked}
                                        disabled={disabled}
                                        onChange={async (e) => {
                                            try {
                                                await toggleItem({
                                                    variables: { idItem: it.id, checked: e.target.checked },
                                                });
                                                await refetch();
                                            } catch (err) {
                                                handleSnackbar(err?.message || "Error", "error");
                                            }
                                        }}
                                    />
                                    <SoftTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            textDecoration: it.checked ? "line-through" : "none",
                                            opacity: it.checked ? 0.7 : 1,
                                        }}
                                    >
                                        {it.texto}
                                    </SoftTypography>
                                    <SoftTypography
                                        variant="caption"
                                        sx={{
                                            ml: 1,
                                            opacity: it.checked ? 0.7 : 1,
                                        }}
                                    >
                                        {it.observacion}
                                    </SoftTypography>
                                </SoftBox>
                            );
                        })
                    ) : (
                        <SoftTypography variant="caption" color="text">
                            Sin items
                        </SoftTypography>
                    )}
                </SoftBox>
            </SoftBox>
        );
    };

    const rowExpandable = (row) => mode === "admin" && Boolean(row?.items?.length || row?.observacion);

    const formattedSelectedDate = formatSelectedDate(selectedDate);

    return (
        <SoftBox p={3}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                <SoftTypography variant="h6">Calendario de tareas</SoftTypography>
            </SoftBox>

            <SoftBox display="grid" gridTemplateColumns="1fr" gap={1}>
                <Card style={{ padding: 16, boxShadow: "none", border: "1px solid #e0e0e0", borderRadius: 8 }}>
                    <SoftBox display="flex" flexDirection="column" gap={3}>
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                            <SoftBox display="flex" alignItems="center" gap={1}>
                                <Icon color="primary">calendar_today</Icon>
                                <SoftTypography component="span" variant="h6" fontWeight="bold">
                                    Calendario
                                </SoftTypography>
                            </SoftBox>

                            <SoftBox display="flex" alignItems="center" gap={1}>
                                <Select value={calendarMonth} onChange={handleCalendarMonthChange} size="small">
                                    {CALENDAR_MONTHS.map((monthName, index) => (
                                        <MenuItem key={monthName} value={index}>
                                            {monthName}
                                        </MenuItem>
                                    ))}
                                </Select>

                                <Select value={calendarYear} onChange={handleCalendarYearChange} size="small">
                                    {yearOptions.map((yearOption) => (
                                        <MenuItem key={yearOption} value={yearOption}>
                                            {yearOption}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </SoftBox>
                        </SoftBox>

                        <SoftBox>
                            <SoftBox display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
                                {CALENDAR_DAYS.map((day) => (
                                    <SoftBox key={day} textAlign="center">
                                        <SoftBox display={{ xs: "none", sm: "block" }}>
                                            <SoftBadge color="uproYellow" badgeContent={day} />
                                        </SoftBox>
                                        <SoftBox display={{ xs: "block", sm: "none" }}>
                                            <SoftBadge color="uproYellow" badgeContent={day.slice(0, 3)} />
                                        </SoftBox>
                                    </SoftBox>
                                ))}
                            </SoftBox>

                            <SoftBox mt={2} display="grid" gridTemplateColumns="repeat(7, 1fr)" rowGap={1.5}>
                                {calendarDays.map(({ date, current }, index) => {
                                    const dateKey = date.format("YYYY-MM-DD");
                                    const dayTasks = tareasByDate.get(dateKey) || [];
                                    const hasEvents = dayTasks.length > 0;
                                    const isSelected = selectedDate && selectedDate.isSame(date, "day");
                                    const estadoDia = aggregateEstadoForDay(dayTasks);

                                    return (
                                        <SoftBox key={`${dateKey}-${index}`} display="flex" justifyContent="center" alignItems="center">
                                            <SoftBox
                                                onClick={current ? () => handleDaySelection(date) : undefined}
                                                sx={{
                                                    position: "relative",
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: current ? "pointer" : "default",
                                                    backgroundColor: isSelected ? "#26c6da" : "transparent",
                                                    color: !current ? "#b0bec5" : isSelected ? "#ffffff" : "#263238",
                                                    transition: "all 0.2s ease-in-out",
                                                    "&:hover": current ? { backgroundColor: isSelected ? "#26c6da" : "#e0f7fa" } : {},
                                                }}
                                            >
                                                <SoftTypography variant="body2" fontWeight="bold">
                                                    {date.date()}
                                                </SoftTypography>

                                                {hasEvents && (
                                                    <SoftBox
                                                        sx={{
                                                            width: "15px",
                                                            height: "15px",
                                                            borderRadius: "100%",
                                                            position: "absolute",
                                                            zIndex: 2,
                                                            top: "0",
                                                            right: "0",
                                                            fontSize: "15px",
                                                        }}
                                                    >
                                                        <Icon
                                                            sx={{
                                                                fontSize: 10,
                                                                color: mode === "admin" ? dotColorByEstado(estadoDia) : "#e91e63",
                                                            }}
                                                        >
                                                            fiber_manual_record
                                                        </Icon>
                                                    </SoftBox>
                                                )}
                                            </SoftBox>
                                        </SoftBox>
                                    );
                                })}
                            </SoftBox>
                        </SoftBox>

                        <SoftBox>
                            <SoftBox position="relative" display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                                <SoftTypography variant="button" fontWeight="bold">
                                    {formattedSelectedDate}
                                </SoftTypography>

                                {mode === "admin" && selectedDate && !selectedDate.isBefore(dayjs(), 'day') && (
                                    <Tooltip title="Agregar tarea" placement="top">
                                        <SoftButton
                                            color="primary"
                                            circular
                                            iconOnly
                                            onClick={handleOpenAddMenu}
                                            sx={{ width: 44, height: 44, minWidth: 44 }}
                                        >
                                            <Icon>add</Icon>
                                        </SoftButton>
                                    </Tooltip>
                                )}
                            </SoftBox>

                            {loading ? (
                                <SoftBox mt={2} display="flex" justifyContent="center" alignItems="center" p={2}>
                                    <SoftTypography variant="body2" color="text">
                                        Cargando tareas...
                                    </SoftTypography>
                                </SoftBox>
                            ) : !selectedDate ? (
                                <SoftBox mt={3} display="flex" alignItems="center" justifyContent="center">
                                    <SoftTypography component="span" variant="body2" color="text">
                                        Selecciona un día para ver las tareas
                                    </SoftTypography>
                                </SoftBox>
                            ) : (
                                <SoftBox mt={2}>
                                    <DataTableCustom
                                        headerStyles={{
                                            backgroundColor: "#ffffff",             // fondo blanco
                                            borderBottom: "1.5px solid #070606ff",
                                            py: 1.1,                               // menos alto
                                            "& .MuiTypography-root": {
                                                fontWeight: 600,                      // negrita media
                                                color: "#344767",                     // gris azulado
                                                // textTransform: "none",                // sin uppercase
                                                fontSize: "0.7rem",
                                                letterSpacing: 0.2,
                                            },
                                        }}
                                        columns={columns}
                                        data={selectedDayTasks}
                                        loading={false}
                                        showActions={false}
                                        zebraStripes
                                        hover
                                        border
                                        maxHeight="400px"
                                        showFooter={true}
                                        emptyMessage={mode === "user" ? "No tienes tareas asignadas para este día" : "No hay tareas para este día"}
                                        expandedRowRender={expandedRowRender}
                                        rowExpandable={rowExpandable}
                                        getRowId={(row) => row.id}
                                        styleCard={{ border: "none" }}
                                    />
                                </SoftBox>
                            )}
                        </SoftBox>
                    </SoftBox>
                </Card>
            </SoftBox>

            <ModalTarea
                open={openModalTarea}
                onClose={() => setOpenModalTarea(false)}
                selectedDate={selectedDate}
                initialTask={editingTask}
                onSaved={async () => {
                    await refetch();
                }}
            />

            <ModalControlCafeteria
                open={openModalControl}
                onClose={() => setOpenModalControl(false)}
                selectedDate={selectedDate}
                initialTask={editingControlTask}
                onSaved={async () => {
                    await refetch();
                }}
            />

            <ModalDetalleTarea
                open={openModalDetalle}
                onClose={() => setOpenModalDetalle(false)}
                task={detailsTask}
                onUpdated={async () => {
                    await refetch();
                }}
            />

            <ModalDetalleControlCafeteria
                open={openModalDetalleControl}
                onClose={() => setOpenModalDetalleControl(false)}
                task={detailsControlTask}
                onUpdated={async () => {
                    await refetch();
                }}
            />
        </SoftBox>
    );
}

export default memo(Calendario);

Calendario.propTypes = {
    mode: PropTypes.oneOf(["admin", "user"]),
    adminFilters: PropTypes.object,
};
